import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import AdminShell from "@/components/admin/AdminShell";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Send,
  Bell,
  Trash2,
  History,
  Users,
  Clock,
  CalendarClock,
  Play,
  X,
} from "lucide-react";

const QUICK_TEMPLATES = [
  {
    label: "Sommer-Aktion",
    title: "Starlink Sommer-Aktion auf Mallorca",
    body: "Nur diesen Monat: -150 € auf jede Installation. Jetzt Termin sichern.",
  },
  {
    label: "Neuer Blog",
    title: "Neuer Artikel im Magazin",
    body: "Lies jetzt: So planst du Premium WLAN für deine Villa.",
  },
  {
    label: "Smart Home Beratung",
    title: "Kostenlose Smart-Home-Beratung",
    body: "Wir kommen vor Ort und planen kostenfrei dein Smart Home Setup.",
  },
];

// Helper: build datetime-local input value from a Date (no timezone suffix).
const toLocalInputValue = (d) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Smart presets relative to now
const presetDate = (kind) => {
  const d = new Date();
  if (kind === "in_1h") d.setHours(d.getHours() + 1);
  if (kind === "tomorrow_10") {
    d.setDate(d.getDate() + 1);
    d.setHours(10, 0, 0, 0);
  }
  if (kind === "saturday_10") {
    const day = d.getDay(); // 0 sun .. 6 sat
    const add = (6 - day + 7) % 7 || 7;
    d.setDate(d.getDate() + add);
    d.setHours(10, 0, 0, 0);
  }
  return toLocalInputValue(d);
};

const SCHED_STATUS_STYLES = {
  scheduled: "bg-sky-100 text-sky-800",
  sending: "bg-amber-100 text-amber-800",
  sent: "bg-emerald-100 text-emerald-800",
  cancelled: "bg-neutral-200 text-neutral-700",
  failed: "bg-red-100 text-red-700",
};

const AdminPush = () => {
  const [subs, setSubs] = useState([]);
  const [history, setHistory] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  // Compose form
  const [form, setForm] = useState({ title: "", body: "", url: "/" });
  const [sendMode, setSendMode] = useState("now"); // "now" | "schedule"
  const [scheduleAt, setScheduleAt] = useState(presetDate("tomorrow_10"));

  const load = async () => {
    setLoading(true);
    try {
      const [s, h, sc] = await Promise.all([
        api.get("/admin/push/subscribers").then((r) => r.data),
        api.get("/admin/push/broadcasts").then((r) => r.data),
        api.get("/admin/push/scheduled").then((r) => r.data),
      ]);
      setSubs(s);
      setHistory(h);
      setScheduled(sc);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60000); // refresh once a minute to reflect scheduler progress
    return () => clearInterval(t);
  }, []);

  const sendBroadcast = async () => {
    if (!form.title || !form.body) {
      toast.error("Titel und Nachricht sind Pflicht.");
      return;
    }
    if (sendMode === "now") {
      if (subs.length === 0) {
        toast.error("Noch keine Subscriber – Banner muss zuerst auf der Website erlaubt werden.");
        return;
      }
      if (!window.confirm(`Push an ${subs.length} Empfänger senden?`)) return;
      setSending(true);
      try {
        const { data } = await api.post("/admin/push/broadcast", form);
        toast.success(`Gesendet: ${data.sent} · Fehlgeschlagen: ${data.failed}`);
        setForm({ title: "", body: "", url: "/" });
        load();
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || err.message);
      } finally {
        setSending(false);
      }
      return;
    }

    // schedule
    if (!scheduleAt) {
      toast.error("Bitte einen Zeitpunkt wählen.");
      return;
    }
    const when = new Date(scheduleAt);
    if (Number.isNaN(when.getTime()) || when.getTime() <= Date.now()) {
      toast.error("Zeitpunkt muss in der Zukunft liegen.");
      return;
    }
    setSending(true);
    try {
      await api.post("/admin/push/schedule", {
        ...form,
        scheduled_at: when.toISOString(),
      });
      toast.success(`Geplant für ${when.toLocaleString()}`);
      setForm({ title: "", body: "", url: "/" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSending(false);
    }
  };

  const cancelScheduled = async (sched) => {
    if (!window.confirm("Geplante Push absagen?")) return;
    try {
      await api.delete(`/admin/push/scheduled/${sched.id}`);
      toast.success("Geplante Push abgesagt");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const runNow = async (sched) => {
    if (!window.confirm("Jetzt sofort senden statt zum geplanten Zeitpunkt?")) return;
    try {
      await api.post(`/admin/push/scheduled/${sched.id}/run-now`);
      toast.success("Push wurde sofort versendet");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const deleteSub = async (id) => {
    if (!window.confirm("Subscriber entfernen?")) return;
    try {
      await api.delete(`/admin/push/subscribers/${id}`);
      setSubs((xs) => xs.filter((x) => x.id !== id));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const upcoming = scheduled.filter((s) => s.status === "scheduled");
  const sendDisabled =
    sending || !form.title || !form.body || (sendMode === "now" && subs.length === 0);

  return (
    <AdminShell
      title="Push Nachrichten"
      subtitle="Sende sofort oder plane Push-Kampagnen für später ein."
    >
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="rounded-3xl bg-white border border-neutral-200 p-6">
          <div className="flex items-center justify-between">
            <Users className="w-5 h-5 text-neutral-500" />
            <Bell className="w-4 h-4 text-emerald-500" />
          </div>
          <div className="mt-5 font-display text-4xl font-light">{subs.length}</div>
          <div className="text-sm text-neutral-500 mt-1">Aktive Subscriber</div>
        </div>
        <div className="rounded-3xl bg-white border border-neutral-200 p-6">
          <CalendarClock className="w-5 h-5 text-neutral-500" />
          <div className="mt-5 font-display text-4xl font-light">{upcoming.length}</div>
          <div className="text-sm text-neutral-500 mt-1">Geplant</div>
        </div>
        <div className="rounded-3xl bg-white border border-neutral-200 p-6">
          <History className="w-5 h-5 text-neutral-500" />
          <div className="mt-5 font-display text-4xl font-light">{history.length}</div>
          <div className="text-sm text-neutral-500 mt-1">Versendete Kampagnen</div>
        </div>
        <div className="rounded-3xl bg-white border border-neutral-200 p-6">
          <Send className="w-5 h-5 text-neutral-500" />
          <div className="mt-5 font-display text-4xl font-light">
            {history.reduce((acc, h) => acc + (h.sent || 0), 0)}
          </div>
          <div className="text-sm text-neutral-500 mt-1">Push gesamt zugestellt</div>
        </div>
      </div>

      <Tabs defaultValue="compose">
        <TabsList className="bg-white rounded-full p-1 border border-neutral-200">
          <TabsTrigger value="compose" data-testid="push-tab-compose">Senden</TabsTrigger>
          <TabsTrigger value="scheduled" data-testid="push-tab-scheduled">
            Geplant ({upcoming.length})
          </TabsTrigger>
          <TabsTrigger value="subscribers" data-testid="push-tab-subscribers">
            Subscriber ({subs.length})
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="push-tab-history">Verlauf</TabsTrigger>
        </TabsList>

        {/* Compose */}
        <TabsContent value="compose" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
            <div className="lg:col-span-3 rounded-3xl bg-white border border-neutral-200 p-6 space-y-5">
              <div>
                <div className="text-xs uppercase tracking-wider text-neutral-500 mb-2">Vorlagen</div>
                <div className="flex flex-wrap gap-2">
                  {QUICK_TEMPLATES.map((tpl) => (
                    <button
                      key={tpl.label}
                      onClick={() => setForm({ ...form, title: tpl.title, body: tpl.body })}
                      data-testid={`push-template-${tpl.label}`}
                      className="text-xs px-3 py-1.5 rounded-full bg-neutral-100 hover:bg-neutral-200"
                    >
                      {tpl.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1">
                <Label>Titel (max. 80 Zeichen)</Label>
                <Input
                  data-testid="push-title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value.slice(0, 80) })}
                  className="rounded-xl h-12"
                />
                <div className="text-[10px] text-neutral-400 text-right">{form.title.length}/80</div>
              </div>
              <div className="space-y-1">
                <Label>Nachricht (max. 300 Zeichen)</Label>
                <Textarea
                  data-testid="push-body"
                  rows={4}
                  value={form.body}
                  onChange={(e) => setForm({ ...form, body: e.target.value.slice(0, 300) })}
                  className="rounded-xl"
                />
                <div className="text-[10px] text-neutral-400 text-right">{form.body.length}/300</div>
              </div>
              <div className="space-y-1">
                <Label>Ziel-URL</Label>
                <Input
                  data-testid="push-url"
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  placeholder="/blog/starlink-mallorca-installation-guide"
                  className="rounded-xl h-12"
                />
              </div>

              {/* Send mode toggle */}
              <div className="rounded-2xl bg-neutral-50 border border-neutral-200 p-4">
                <div className="flex gap-2 mb-4">
                  <button
                    type="button"
                    onClick={() => setSendMode("now")}
                    data-testid="push-mode-now"
                    className={`flex-1 text-sm px-4 py-2.5 rounded-xl font-medium ${
                      sendMode === "now"
                        ? "bg-neutral-950 text-white"
                        : "bg-white text-neutral-700 border border-neutral-200"
                    }`}
                  >
                    Sofort senden
                  </button>
                  <button
                    type="button"
                    onClick={() => setSendMode("schedule")}
                    data-testid="push-mode-schedule"
                    className={`flex-1 text-sm px-4 py-2.5 rounded-xl font-medium ${
                      sendMode === "schedule"
                        ? "bg-neutral-950 text-white"
                        : "bg-white text-neutral-700 border border-neutral-200"
                    }`}
                  >
                    Einplanen
                  </button>
                </div>

                {sendMode === "schedule" && (
                  <div className="space-y-3" data-testid="push-schedule-pane">
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setScheduleAt(presetDate("in_1h"))}
                        className="text-xs px-3 py-1.5 rounded-full bg-white border border-neutral-200 hover:bg-neutral-100"
                        data-testid="push-preset-1h"
                      >
                        In 1 Stunde
                      </button>
                      <button
                        type="button"
                        onClick={() => setScheduleAt(presetDate("tomorrow_10"))}
                        className="text-xs px-3 py-1.5 rounded-full bg-white border border-neutral-200 hover:bg-neutral-100"
                        data-testid="push-preset-tomorrow"
                      >
                        Morgen 10:00
                      </button>
                      <button
                        type="button"
                        onClick={() => setScheduleAt(presetDate("saturday_10"))}
                        className="text-xs px-3 py-1.5 rounded-full bg-white border border-neutral-200 hover:bg-neutral-100"
                        data-testid="push-preset-saturday"
                      >
                        Samstag 10:00
                      </button>
                    </div>
                    <Input
                      type="datetime-local"
                      data-testid="push-schedule-input"
                      value={scheduleAt}
                      onChange={(e) => setScheduleAt(e.target.value)}
                      className="rounded-xl h-12 bg-white"
                    />
                    <div className="text-xs text-neutral-500 flex items-center gap-1.5">
                      <Clock className="w-3 h-3" />
                      {scheduleAt
                        ? `Wird gesendet am ${new Date(scheduleAt).toLocaleString()}`
                        : "Bitte einen Zeitpunkt wählen."}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={sendBroadcast}
                  disabled={sendDisabled}
                  data-testid="push-send"
                  className="inline-flex items-center gap-2 rounded-full bg-neutral-950 text-white px-6 py-3 text-sm font-medium hover:bg-black disabled:opacity-60"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : sendMode === "now" ? (
                    <Send className="w-4 h-4" />
                  ) : (
                    <CalendarClock className="w-4 h-4" />
                  )}
                  {sendMode === "now"
                    ? `An ${subs.length} Empfänger senden`
                    : "Push einplanen"}
                </button>
              </div>
            </div>

            {/* Preview */}
            <div className="lg:col-span-2 rounded-3xl bg-neutral-950 text-white p-6">
              <div className="text-xs uppercase tracking-wider text-sky-300 mb-4">Vorschau</div>
              <div className="rounded-2xl bg-neutral-900 border border-white/10 p-4 flex gap-3">
                <div className="w-10 h-10 rounded-xl bg-sky-600 grid place-items-center flex-shrink-0">
                  <Bell className="w-5 h-5" />
                </div>
                <div className="min-w-0">
                  <div className="text-xs text-neutral-400">
                    Mallorca Digital ·{" "}
                    {sendMode === "schedule" && scheduleAt
                      ? new Date(scheduleAt).toLocaleString()
                      : "jetzt"}
                  </div>
                  <div className="mt-1 font-medium leading-tight line-clamp-2">
                    {form.title || "Titel deiner Nachricht"}
                  </div>
                  <div className="mt-1 text-sm text-neutral-300 leading-snug line-clamp-3">
                    {form.body || "Inhalt der Push-Benachrichtigung erscheint hier in der Vorschau."}
                  </div>
                </div>
              </div>
              <div className="mt-5 text-xs text-neutral-400 leading-relaxed">
                So sieht die Nachricht auf einem Android-/iOS-Smartphone aus. Push erreicht nur
                Besucher, die das Banner auf der Website erlaubt haben.
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Scheduled */}
        <TabsContent value="scheduled" className="mt-6">
          <div className="rounded-3xl bg-white border border-neutral-200 overflow-hidden">
            {loading ? (
              <div className="p-12 grid place-items-center text-neutral-400">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : scheduled.length === 0 ? (
              <div className="p-12 text-center text-neutral-500" data-testid="push-scheduled-empty">
                Keine geplanten Push-Nachrichten.
              </div>
            ) : (
              <Table data-testid="push-scheduled-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Zeitpunkt</TableHead>
                    <TableHead>Titel</TableHead>
                    <TableHead>Nachricht</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {scheduled.map((s) => (
                    <TableRow key={s.id} data-testid={`push-scheduled-row-${s.id}`}>
                      <TableCell className="whitespace-nowrap text-sm">
                        <div className="flex items-center gap-1.5 text-neutral-700">
                          <Clock className="w-3.5 h-3.5 text-sky-700" />
                          {new Date(s.scheduled_at).toLocaleString()}
                        </div>
                        {s.sent_at && (
                          <div className="text-[11px] text-neutral-500 mt-0.5">
                            Versendet: {new Date(s.sent_at).toLocaleString()}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="font-medium max-w-xs">
                        <div className="line-clamp-1">{s.title}</div>
                      </TableCell>
                      <TableCell className="text-neutral-700 max-w-md">
                        <div className="line-clamp-2 text-sm">{s.body}</div>
                      </TableCell>
                      <TableCell>
                        <Badge className={`rounded-full ${SCHED_STATUS_STYLES[s.status] || ""}`}>
                          {s.status}
                        </Badge>
                        {s.result && (
                          <div className="text-[10px] text-neutral-500 mt-1">
                            {s.result.sent}/{s.result.total} zugestellt
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        {s.status === "scheduled" ? (
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => runNow(s)}
                              data-testid={`push-scheduled-run-${s.id}`}
                              className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-800 text-xs"
                            >
                              <Play className="w-3 h-3" />
                              Jetzt senden
                            </button>
                            <button
                              onClick={() => cancelScheduled(s)}
                              data-testid={`push-scheduled-cancel-${s.id}`}
                              className="inline-flex items-center gap-1 text-red-600 hover:text-red-700 text-xs"
                            >
                              <X className="w-3 h-3" />
                              Absagen
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-neutral-400">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        {/* Subscribers */}
        <TabsContent value="subscribers" className="mt-6">
          <div className="rounded-3xl bg-white border border-neutral-200 overflow-hidden">
            {loading ? (
              <div className="p-12 grid place-items-center text-neutral-400">
                <Loader2 className="w-5 h-5 animate-spin" />
              </div>
            ) : subs.length === 0 ? (
              <div className="p-12 text-center text-neutral-500" data-testid="push-subs-empty">
                Noch keine Subscriber. Sobald Besucher auf der Website das Push-Banner erlauben,
                erscheinen sie hier.
              </div>
            ) : (
              <Table data-testid="push-subs-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Sprache</TableHead>
                    <TableHead>Endpoint</TableHead>
                    <TableHead>Browser</TableHead>
                    <TableHead>Abonniert</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {subs.map((s) => (
                    <TableRow key={s.id} data-testid={`push-sub-${s.id}`}>
                      <TableCell className="uppercase font-medium text-xs">{s.locale}</TableCell>
                      <TableCell className="font-mono text-xs text-neutral-500 max-w-xs truncate">
                        {s.endpoint}
                      </TableCell>
                      <TableCell className="text-xs text-neutral-600 max-w-xs truncate">
                        {s.user_agent}
                      </TableCell>
                      <TableCell className="text-xs text-neutral-500 whitespace-nowrap">
                        {new Date(s.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <button
                          onClick={() => deleteSub(s.id)}
                          data-testid={`push-sub-delete-${s.id}`}
                          className="text-red-600 hover:text-red-700 text-xs inline-flex items-center gap-1"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Entfernen
                        </button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>

        {/* History */}
        <TabsContent value="history" className="mt-6">
          <div className="rounded-3xl bg-white border border-neutral-200 overflow-hidden">
            {history.length === 0 ? (
              <div className="p-12 text-center text-neutral-500" data-testid="push-history-empty">
                Noch keine Kampagnen versendet.
              </div>
            ) : (
              <Table data-testid="push-history-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Titel</TableHead>
                    <TableHead>Nachricht</TableHead>
                    <TableHead>Ziel</TableHead>
                    <TableHead>Gesendet</TableHead>
                    <TableHead>Fehler</TableHead>
                    <TableHead>Datum</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {history.map((h) => (
                    <TableRow key={h.id}>
                      <TableCell className="font-medium max-w-xs">
                        <div className="line-clamp-1">{h.title}</div>
                      </TableCell>
                      <TableCell className="text-neutral-700 max-w-md">
                        <div className="line-clamp-2 text-sm">{h.body}</div>
                      </TableCell>
                      <TableCell className="text-xs text-sky-700">{h.url}</TableCell>
                      <TableCell className="text-emerald-700 font-medium">{h.sent}</TableCell>
                      <TableCell className="text-neutral-500">{h.failed}</TableCell>
                      <TableCell className="text-xs text-neutral-500 whitespace-nowrap">
                        {new Date(h.created_at).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </AdminShell>
  );
};

export default AdminPush;
